import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAgency, updateAgency, uploadAgencyLogo, type Agency, type UpdateAgencyParams } from '@/api/agency'
import { useAuthStore } from '@/features/auth/authStore'
import type { AgencyView } from '@/api/types'

function toView(a: Agency): AgencyView {
  return {
    id: a.id,
    name: a.name,
    slug: a.slug,
    industry: a.industry,
    brandColor: a.brandColor,
    brandColorDark: a.brandColorDark,
    brandColor50: a.brandColor50,
    logoUrl: a.logoUrl,
    currency: a.currency,
    dealContactRoles: a.dealContactRoles,
  }
}

/** Fresh agency from the server — the persisted auth-store copy can be stale. */
export function useAgencyQuery() {
  return useQuery({ queryKey: ['agency'], queryFn: getAgency })
}

export function useUpdateAgency() {
  const qc = useQueryClient()
  const setAgency = useAuthStore((s) => s.setAgency)
  return useMutation({
    mutationFn: (params: UpdateAgencyParams) => updateAgency(params),
    onSuccess: (a) => {
      // updating the store re-applies the brand via the App-level effect
      setAgency(toView(a))
      qc.setQueryData(['agency'], a)
    },
  })
}

export function useUploadAgencyLogo() {
  const qc = useQueryClient()
  const setAgency = useAuthStore((s) => s.setAgency)
  return useMutation({
    mutationFn: (file: File) => uploadAgencyLogo(file),
    onSuccess: (a) => {
      setAgency(toView(a)) // refreshes the sidebar logo immediately
      qc.setQueryData(['agency'], a)
    },
  })
}
